import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lightbox from "./Lightbox";

const t = {
  es: {
    label: "Galería",
    title: "Descubre nuestros",
    titleAccent: "espacios",
    filters: ["Todo", "Piscina", "Bungalow", "Jacuzzi"],
  },
  en: {
    label: "Gallery",
    title: "Discover our",
    titleAccent: "spaces",
    filters: ["All", "Piscina", "Bungalow", "Jacuzzi"],
  },
};

const images = [
  { src: "/images/galeria/B1.jpg", cat: "Bungalow", title: "Entrada principal" },
  { src: "/images/galeria/B2.jpg", cat: "Bungalow", title: "Pórtico Bungalow" },
  { src: "/images/galeria/B3.jpg", cat: "Bungalow", title: "Sala" },
  { src: "/images/galeria/B4.jpg", cat: "Bungalow", title: "Cocina" },
  { src: "/images/galeria/B5.jpg", cat: "Bungalow", title: "Baño" },
  { src: "/images/galeria/B6.jpg", cat: "Bungalow", title: "Cuarto principal" },
  { src: "/images/galeria/B7.jpg", cat: "Bungalow", title: "Cuarto secundario" },
  { src: "/images/galeria/B8.jpg", cat: "Bungalow", title: "Cuarto secundario" },
  { src: "/images/galeria/J1.jpg", cat: "Jacuzzi", title: "Comedor privado" },
  { src: "/images/galeria/J2.jpg", cat: "Jacuzzi", title: "Churrasquera" },
  { src: "/images/galeria/J3.jpg", cat: "Jacuzzi", title: "Jacuzzi" },
  { src: "/images/galeria/P1.jpg", cat: "Piscina", title: "Piscina Común" },
  { src: "/images/galeria/P2.jpg", cat: "Piscina", title: "Piscina Común" },
];

export default function Gallery({ lang }) {
  const tr = t[lang];
  const filterKeys = ["Todo", "Piscina", "Bungalow", "Jacuzzi"];
  const [active, setActive] = useState(filterKeys[0]);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const filtered =
    active === "Todo" ? images : images.filter((img) => img.cat === active);

  const openLightbox = (src) => {
    // Find index in the filtered list
    const idx = filtered.findIndex((img) => img.src === src);
    setLightboxIdx(idx);
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-12">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-montserrat text-[#8C5E35] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            {tr.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-montserrat font-extrabold text-[#1F3A5E] text-4xl md:text-5xl leading-tight"
            data-testid="gallery-title"
          >
            {tr.title} <span className="text-[#8C5E35]">{tr.titleAccent}</span>
          </motion.h2>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center justify-center gap-3 flex-wrap mb-10">
          {filterKeys.map((key, i) => (
            <button
              key={key}
              data-testid={`gallery-filter-${key.toLowerCase()}`}
              onClick={() => setActive(key)}
              className={`px-5 py-2 rounded-full font-montserrat text-sm font-semibold transition-all duration-200 ${
                active === key
                  ? "bg-[#1F3A5E] text-white shadow-lg shadow-[#1F3A5E]/20"
                  : "bg-[#FAFAF9] text-[#1F3A5E]/60 border border-[#1F3A5E]/10 hover:text-[#1F3A5E]"
              }`}
            >
              {tr.filters[i]}
            </button>
          ))}
        </div>

        {/* Image Grid */}
        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          <AnimatePresence mode="popLayout">
            {filtered.map((img) => (
              <motion.div
                key={img.src}
                layout
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.35 }}
                data-testid={`gallery-img-${img.cat.toLowerCase()}`}
                className="relative rounded-2xl overflow-hidden group cursor-pointer aspect-square"
                onClick={() => openLightbox(img.src)}
              >
                <img
                  src={img.src}
                  alt={img.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A5E]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                  <span className="font-dm-sans text-white text-sm font-medium">
                    {img.title}
                  </span>
                  <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                    </svg>
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && (
        <Lightbox
          images={filtered}
          currentIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % filtered.length)}
        />
      )}
    </section>
  );
}
